import { Dispatch, FC, SetStateAction, useEffect, useMemo, useState } from 'react';
import { PreloadedQuery, usePreloadedQuery } from 'react-relay';
import { DiagnosticsGetStudentsTableQuery } from '../../graphQLOperations/__generated__/DiagnosticsGetStudentsTableQuery.graphql';
import { DiagnosticsStatusByClassReportQuery } from '../../graphQLOperations/__generated__/DiagnosticsStatusByClassReportQuery.graphql';
import {
  getDiagnosticStatusByClassReport,
  getStudentsDiganosticReportTableInfo,
} from '../../graphQLOperations/Diagnostics';
import { DataTable, DataTableSelectionMultipleChangeEvent } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { format } from 'date-fns';
import { FilterMatchMode } from 'primereact/api';
import { ProjectionGroup } from '../../models/ProjectionGroupEnum';
import { normalizeProjectionGroup } from '../../businessLogicService/normalizeProjectionGroup';
import StudentNameLink from '../common/StudentNameLink/StudentNameLink';
import { MaskedStudentData } from '../MaskedData/MaskedStudentData';
import { PiiTypes } from '../../analytics/dataMasking';
import { ProjectionSettings } from './StudentsCompletedGateWrapper';
import { Button } from 'primereact/button';
import DiagnosticReportStudentTableHeader from './DiagnosticReportStudentTableHeader';
import { splitId } from '../../businessLogicService/splitIdsFromBackEnd';
import { DownloadData } from './DiagnosticReportDownloadCSV';
import { buildDiagnosticDownloadData } from './reportHelper';

interface Props {
  studentsTableQueryRef: PreloadedQuery<DiagnosticsGetStudentsTableQuery, Record<string, unknown>>;
  inProgressQueryRef: PreloadedQuery<DiagnosticsStatusByClassReportQuery, Record<string, unknown>>;
  projectionSettings: ProjectionSettings;
  setRefetchKey: Dispatch<SetStateAction<number>>;
  classIds?: string[];
}

const projectionOptions: { key: ProjectionGroup; label: string; icon: JSX.Element }[] = [
  {
    key: ProjectionGroup.AT_RISK,
    label: 'Approaching',
    icon: (
      <span className="tw-text-xs tw-font-atkinson tw-font-bold tw-px-3 tw-py-1 tw-bg-amber-50 tw-border tw-border-solid tw-rounded-lg tw-border-amber-400 !tw-text-neutral-950 tw-w-[95px] tw-text-center">
        Approaching
      </span>
    ),
  },
  {
    key: ProjectionGroup.ON_TRACK,
    label: 'On track',
    icon: (
      <span className="tw-text-xs tw-font-atkinson tw-font-bold tw-px-3 tw-py-1 tw-bg-sky-50 tw-border tw-border-solid tw-rounded-lg tw-border-sky-200 !tw-text-neutral-950 tw-w-[95px] tw-text-center">
        On track
      </span>
    ),
  },
];

export interface TableDataElement {
  [key: string]: string | null | ProjectionGroup | undefined | boolean | Date | number;
  id: string;
  name: string | null | undefined;
  firstName: string;
  lastName: string;
  publicUserId: string;
  status: boolean;
  date: Date;
  demonstratedSkills: number | null | undefined;
  projectedStatus: ProjectionGroup | null | undefined;
  threeSkills: ProjectionGroup | null | undefined;
  twoSkills: ProjectionGroup | null | undefined;
  lastDiagnosticId?: number | null;
  retakeAssigned?: boolean | null;
  timeSpent?: number | null;
  roadmapSkills?: number | null;
}

export interface SortOrder {
  field: string;
  order: 1 | -1 | 0;
}

export const DiagnosticReportStudentsTable: FC<Props> = ({
  studentsTableQueryRef,
  inProgressQueryRef,
  projectionSettings,
  setRefetchKey,
  classIds,
}) => {
  const [filters, setFilters] = useState<{ name: { value: string | null; matchMode: 'contains' } }>({
    name: { value: null, matchMode: FilterMatchMode.CONTAINS },
  });
  const [sortValue, setSortValue] = useState<SortOrder>({ field: 'lastName', order: 1 });
  const [tableData, setTableData] = useState<TableDataElement[]>([]);
  const [downloadData, setDownloadData] = useState<DownloadData[]>([]);
  const { classDiagnosticReport } = usePreloadedQuery(getStudentsDiganosticReportTableInfo, studentsTableQueryRef);
  const { classDiagnosticStatusReport } = usePreloadedQuery(getDiagnosticStatusByClassReport, inProgressQueryRef);

  const [selectedStudents, setSelectedStudents] = useState<TableDataElement[]>([]);
  const [activeSelection, setActiveSelection] = useState(false);
  const [allStudentsSelected, setAllStudentsSelected] = useState(false);
  const [filterStatus, setFilterStatus] = useState<ProjectionGroup>(ProjectionGroup.ALL);
  const [filterCountSkills, setFilterCountSkills] = useState<2 | 3>(2);

  useEffect(() => {
    const tableData: TableDataElement[] = classDiagnosticReport
      .filter(el => el.diagnosticCompleted)
      .map(item => {
        const firstName = item.student.firstName ?? '';
        const lastName = item.student.lastName ?? '';
        const timeSpent = classDiagnosticStatusReport.find(el => splitId(el.student.id) === splitId(item.student.id))
          ?.totalActiveTime;
        return {
          id: item.student.id,
          name: `${firstName} ${lastName}`,
          firstName,
          lastName,
          publicUserId: item.student.publicUserId ?? '',
          status: item.diagnosticCompleted,
          date: item.diagnosticDate,
          demonstratedSkills: item.demonstratedSkills,
          projectedStatus: normalizeProjectionGroup(
            item[projectionSettings.threeSkillsPerWeek ? 'projectedStatusThreeSkills' : 'projectedStatusTwoSkills'],
          ),
          lastDiagnosticId: item.lastDiagnosticId,
          retakeAssigned: item.retakeAssigned,
          threeSkills: normalizeProjectionGroup(item.projectedStatusThreeSkills),
          twoSkills: normalizeProjectionGroup(item.projectedStatusTwoSkills),
          timeSpent: timeSpent !== undefined && timeSpent !== null ? Math.round(timeSpent / 60) : null,
          roadmapSkills: item.roadmapSkills,
        };
      });

    if (projectionSettings.selectedStatus !== ProjectionGroup.NOTSET) {
      return setTableData(tableData.filter(item => item.projectedStatus === projectionSettings.selectedStatus));
    } else {
      return setTableData(tableData);
    }
  }, [projectionSettings, classDiagnosticReport, classDiagnosticStatusReport]);

  useEffect(() => {
    setDownloadData(buildDiagnosticDownloadData(classDiagnosticStatusReport, classDiagnosticReport));
  }, [classDiagnosticStatusReport, classDiagnosticReport]);

  const classRoadmapSkills = classDiagnosticReport.find(element => element.roadmapSkills)?.roadmapSkills || 0;

  const filteredTableData = useMemo(() => {
    if (filterStatus === ProjectionGroup.ALL) return tableData;
    const column: 'twoSkills' | 'threeSkills' = filterCountSkills === 2 ? 'twoSkills' : 'threeSkills';
    const target = filterStatus === ProjectionGroup.APPROACHING ? ProjectionGroup.AT_RISK : filterStatus;
    return tableData.filter(item => item[column] === target);
  }, [tableData, filterStatus, filterCountSkills]);

  useEffect(() => {
    const completedStudents = tableData.filter(st => st.status);
    if (selectedStudents.length === completedStudents.length) {
      setAllStudentsSelected(true);
    } else {
      setAllStudentsSelected(false);
    }
  }, [selectedStudents, tableData]);

  return (
    <div className="tw-w-full tw-flex tw-flex-col tw-gap-y-6">
      <div className="tw-flex lg:tw-flex-row tw-flex-col lg:tw-items-center tw-gap-y-2 tw-py-4 tw-px-5 tw-border-2 tw-border-solid tw-rounded-2xl tw-border-neutral-200 tw-w-full tw-justify-between">
        <div className="tw-flex tw-flex-col">
          <p className="tw-m-0 tw-font-atkinson tw-text-base tw-text-neutral-950 tw-font-bold">
            Projected performance pace
          </p>
          <p className="tw-m-0 tw-font-atkinson tw-text-sm tw-text-neutral-700">
            {`How many skills students complete per week changes who is on pace to reach 75% (${Math.round(
              classRoadmapSkills * 0.75,
            )} of ${classRoadmapSkills}) Roadmap
            completion by year-end.`}
          </p>
        </div>
        <span className="tw-min-w-[240px] tw-border-2 tw-h-[45px] tw-border-solid tw-rounded-[30px] tw-border-neutral-200 tw-p-1 tw-bg-neutral-50 tw-flex tw-flex-row tw-gap-x-2">
          <Button
            className={`!tw-py-1 !tw-px-3 tw-w-[50%] !tw-rounded-[16px] !tw-border-2 !tw-text-sm !tw-font-bold !tw-font-atkinson 
              ${
                filterCountSkills === 2
                  ? '!tw-border-indigo-400 tw-shadow-[0_0_0_2px_#BFC9FF]'
                  : '!tw-border-neutral-100'
              }`}
            outlined
            label="2 skill/week"
            onClick={() => setFilterCountSkills(2)}
          />
          <Button
            className={`!tw-py-1 !tw-px-3 tw-w-[50%] !tw-rounded-[16px] !tw-border-2 !tw-text-sm !tw-font-bold !tw-font-atkinson
              ${
                filterCountSkills === 3
                  ? '!tw-border-indigo-400 tw-shadow-[0_0_0_2px_#BFC9FF]'
                  : '!tw-border-neutral-100'
              }`}
            outlined
            label="3 skill/week"
            onClick={() => setFilterCountSkills(3)}
          />
        </span>
      </div>
      <DataTable
        sortField={sortValue.field}
        sortOrder={sortValue.order}
        onSort={sV => {
          setSortValue({ field: sV.sortField, order: sV.sortOrder || 1 });
        }}
        onSelectAllChange={() => {
          if (allStudentsSelected) {
            setSelectedStudents([]);
            setAllStudentsSelected(false);
          } else {
            setSelectedStudents(tableData);
            setAllStudentsSelected(true);
          }
        }}
        selectAll={allStudentsSelected}
        selectionMode={'checkbox'}
        selection={selectedStudents}
        showSelectionElement={item => item.status || null}
        onSelectionChange={(e: DataTableSelectionMultipleChangeEvent<TableDataElement[]>) => {
          if (activeSelection) setSelectedStudents(e.value);
        }}
        pt={{
          header: { className: '!tw-bg-white !tw-border-none !tw-p-0 !tw-pb-4' },
          wrapper: { className: '!tw-border !tw-border-solid !tw-border-neutral-200 !tw-rounded-[20px]' },
        }}
        dataKey="id"
        header={
          <DiagnosticReportStudentTableHeader
            filters={filters}
            setFilters={setFilters}
            tableData={tableData}
            downloadData={downloadData}
            setRefetchKey={setRefetchKey}
            sortValue={sortValue}
            setSelectedStudents={setSelectedStudents}
            selectedStudents={selectedStudents}
            activeSelection={activeSelection}
            setActiveSelection={setActiveSelection}
            filterStatus={filterStatus}
            setFilterStatus={setFilterStatus}
            filterCountSkills={filterCountSkills}
          />
        }
        className="p-1"
        filters={filters}
        style={{ fontFamily: 'Poppins' }}
        paginator
        rows={10}
        rowsPerPageOptions={[5, 10, 25, 50]}
        value={filteredTableData}
        emptyMessage="No students found."
        stripedRows
        paginatorTemplate="PrevPageLink CurrentPageReport NextPageLink RowsPerPageDropdown"
        currentPageReportTemplate="{first} to {last} of {totalRecords}"
      >
        {activeSelection && <Column selectionMode="multiple" headerStyle={{ width: '3rem' }}></Column>}
        <Column
          sortable
          field="name"
          sortField="lastName"
          header="Name"
          body={(row: TableDataElement) => (
            <StudentNameLink
              studentId={row.id}
              classIds={classIds}
              className={`${activeSelection ? '[tr:hover_&]:tw-text-blue-700' : '[tr:hover_&]:tw-text-white'} `}
            >
              <MaskedStudentData
                student={row}
                type={PiiTypes.FullName}
                className={`${activeSelection ? '[tr:hover_&]:tw-text-blue-700' : '[tr:hover_&]:tw-text-white'} `}
              >
                {row.name}
              </MaskedStudentData>
            </StudentNameLink>
          )}
        />
        <Column
          sortable
          header="Date completed"
          field="date"
          body={item => <span>{item.date ? format(item.date, 'P') : ''}</span>}
        />
        <Column
          sortable
          header="Time spent"
          field="timeSpent"
          body={(student: TableDataElement) => <span>{student.timeSpent ? student.timeSpent + 'm' : '-'}</span>}
        />
        <Column
          sortable
          field="demonstratedSkills"
          header={
            <div className="tw-flex tw-flex-col tw-items-center">
              <h1 className="tw-text-sm tw-font-atkinson tw-text-neutral-950 tw-font-bold">Result</h1>
              <p className="tw-m-0 tw-text-xs tw-font-atkinson tw-text-neutral-600 tw-font-bold">Demonstrated skills</p>
            </div>
          }
          dataType="number"
          body={item => (
            <span>
              <span className="tw-font-bold">{item.demonstratedSkills}</span>/{item.roadmapSkills}
            </span>
          )}
        />
        <Column
          className="w-2"
          header={
            <div className="tw-flex tw-flex-col tw-items-center">
              <h1 className="tw-text-sm tw-font-atkinson tw-text-neutral-950 tw-font-bold">Baseline projection</h1>
              <p className="tw-m-0 tw-text-xs tw-font-atkinson tw-text-neutral-600 tw-font-bold">
                at {filterCountSkills === 2 ? '2' : '3'} skills/week
              </p>
            </div>
          }
          body={item => {
            const typeOptionThree = projectionOptions.find(element => element.key === item.threeSkills);
            const typeOptionTwo = projectionOptions.find(element => element.key === item.twoSkills);
            return (
              <div className="flex align-items-center h-3rem">
                <div className="flex align-items-center">
                  {filterCountSkills === 2 ? <span>{typeOptionTwo?.icon}</span> : <span>{typeOptionThree?.icon}</span>}
                </div>
              </div>
            );
          }}
        />
      </DataTable>
    </div>
  );
};
